import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Bars3Icon,
  UserCircleIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSettings } from '../contexts/SettingsContext.tsx';

// Função utilitária para construir URLs do backend
const getBackendUrl = (path: string) => {
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  return `${baseUrl}${path}`;
};

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { settings } = useSettings();

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
  };

  return (
    <header className="bg-white shadow">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <span className="sr-only">Abrir menu</span>
              <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
            </button>
            <div className="flex-shrink-0 flex items-center">
              {settings?.logoUrl ? (
                <img
                  src={
                    settings.logoUrl.startsWith('/uploads/')
                      ? getBackendUrl(settings.logoUrl)
                      : settings.logoUrl
                  }
                  alt={settings.companyName}
                  className="h-8 w-auto mr-2"
                />
              ) : null}
              <h1 className="text-xl font-bold text-gray-800 md:hidden">
                {settings?.companyName || 'Gestão Patrimonial'}
              </h1>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {/* User Menu */}
            <div className="relative">
              <button
                type="button"
                className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              >
                <span className="hidden md:block text-sm font-medium text-gray-700 mr-2">
                  {user?.name || 'Usuário'}
                </span>
                {(() => {
                  console.log('Header - user.avatar:', user?.avatar);
                  console.log('Header - user.avatar type:', typeof user?.avatar);
                  console.log('Header - user object:', user);
                  return null;
                })()}
                {user?.avatar ? (
                  <img
                    src={
                      user.avatar &&
                      typeof user.avatar === 'string' &&
                      user.avatar.startsWith('/uploads/')
                        ? getBackendUrl(user.avatar)
                        : user.avatar
                    }
                    alt={user.name || 'Avatar'}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-white">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                )}
                <ChevronDownIcon className="ml-1 h-4 w-4 text-gray-400" />
              </button>

              {/* User Dropdown */}
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 z-50">
                  <div className="p-4 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900 truncate" title={user?.name || 'Usuário'}>{user?.name || 'Usuário'}</p>
                    <p className="text-sm text-gray-500 truncate" title={user?.email || 'usuario@exemplo.com'}>{user?.email || 'usuario@exemplo.com'}</p>
                  </div>
                  <div className="py-2">
                    <NavLink
                      to="/profile"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <UserCircleIcon className="h-4 w-4 mr-3" />
                      Meu Perfil
                    </NavLink>
                  </div>
                  <div className="border-t border-gray-200 py-2">
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50 transition-colors"
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                      Sair
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu, show/hide based on menu state */}
      {isMobileMenuOpen && (
        <div className="md:hidden">
          <div className="pt-2 pb-3 space-y-1">
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                `${
                  isActive
                    ? 'bg-primary-50 border-primary-500 text-primary-700'
                    : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                } block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors duration-200`
              }
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Dashboard
            </NavLink>
            <NavLink
              to="/assets"
              className={({ isActive }) =>
                `${
                  isActive
                    ? 'bg-primary-50 border-primary-500 text-primary-700'
                    : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                } block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors duration-200`
              }
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Patrimônio
            </NavLink>
            <NavLink
              to="/vehicles"
              className={({ isActive }) =>
                `${
                  isActive
                    ? 'bg-primary-50 border-primary-500 text-primary-700'
                    : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                } block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors duration-200`
              }
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Veículos
            </NavLink>
            <NavLink
              to="/debts"
              className={({ isActive }) =>
                `${
                  isActive
                    ? 'bg-primary-50 border-primary-500 text-primary-700'
                    : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                } block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors duration-200`
              }
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Dívidas
            </NavLink>
            <NavLink
              to="/receivables"
              className={({ isActive }) =>
                `${
                  isActive
                    ? 'bg-primary-50 border-primary-500 text-primary-700'
                    : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                } block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors duration-200`
              }
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Recebíveis
            </NavLink>
            <NavLink
              to="/new-reports"
              className={({ isActive }) =>
                `${
                  isActive
                    ? 'bg-primary-50 border-primary-500 text-primary-700'
                    : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                } block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors duration-200`
              }
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Relatórios
            </NavLink>
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                `${
                  isActive
                    ? 'bg-primary-50 border-primary-500 text-primary-700'
                    : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                } block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors duration-200`
              }
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Configurações
            </NavLink>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
